import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";
import { Grid, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress, Box, TableContainer } from "@material-ui/core";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
import { getStandardButtonStyles } from "../../styles/standardButtons";
// import { SocketContext } from "../../context/Socket/SocketContext";


const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    return action.payload;
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
}));

const ContactListItems = () => {
  const classes = useStyles();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);
  
  // Nuevos estados para mejoras de UX en importación
  const [importing, setImporting] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [triggerReload, setTriggerReload] = useState(0);
  const [contactsCount, setContactsCount] = useState(0);

  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setContactsCount(data.count);
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId, triggerReload]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCompanyContactLists = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }
    }
    socket.on(`company-${companyId}-ContactListItem`, onCompanyContactLists);

    return () => {
      socket.off(`company-${companyId}-ContactListItem`, onCompanyContactLists);
    };
  }, [contactListId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    setTriggerReload((prev) => prev + 1);
  };

  const handleImportContacts = async () => {
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      const { data } = await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });

      setImporting(false);
      
      // Mostrar modal persistente con las estadísticas de la importación
      setAlertTitle(i18n.t("contactListItems.importDialog.completedTitle"));
      setAlertMessage(i18n.t("contactListItems.importDialog.completedMessage", { total: data.total, imported: data.imported, invalid: data.invalid }));
      setAlertOpen(true);

      // Resetear filtros para recargar automáticamente en pantalla sin refrescar la página
      setSearchParam("");
      setPageNumber(1);
      dispatch({ type: "RESET" });
      setTriggerReload((prev) => prev + 1);
    } catch (err) {
      setImporting(false);
      
      // Interceptamos el error del límite para mostrarlo en un Dialog persistente en vez de Toast rápido
      const errorMsg = err.response?.data?.error || err.message || "";
      if (errorMsg.includes("250")) {
        setAlertTitle(i18n.t("contactListItems.importDialog.limitTitle"));
        setAlertMessage(i18n.t("contactListItems.importDialog.limitMessage"));
        setAlertOpen(true);
      } else {
        toastError(err);
      }
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${deletingContact.name
            }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <br />
            <br />
            <span style={{ color: '#ff9800', fontWeight: 'bold' }}>⚠️ Límite estricto: El sistema procesará un máximo de 250 contactos por archivo Excel para evitar bloqueos.</span>
            <br />
            <br />
            <a href={planilhaExemplo} download="planilha.xlsx">
              Clique aqui para baixar planilha exemplo.
            </a>
          </>
        )}
      </ConfirmationModal>
      {
        user.profile === "user" && user?.showCampaign === "disabled" ?
          <ForbiddenPage />
          :
          <>
            <Paper
              className={classes.mainPaper}
              variant="outlined"
            >
              <MainHeader>
                <Grid style={{ width: "99.6%" }} container alignItems="center" spacing={1}>
                  <Grid xs={12} sm={4} item>
                    <Title>{contactList.name}</Title>
                  </Grid>
                  <Grid xs={12} sm={8} item>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: 10, flexWrap: "wrap" }}>
                      <TextField
                        placeholder={i18n.t("contactListItems.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon style={{ color: "gray" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        className={classes.secondaryButton}
                        onClick={goToContactLists}
                      >
                        {i18n.t("contactListItems.buttons.lists")}
                      </Button>
                      <Button
                        className={classes.secondaryButton}
                        onClick={() => {
                          fileUploadRef.current.value = null;
                          fileUploadRef.current.click();
                        }}
                      >
                        {i18n.t("contactListItems.buttons.import")}
                      </Button>
                      <Button
                        className={classes.applyButton}
                        onClick={handleOpenContactListItemModal}
                      >
                        {i18n.t("contactListItems.buttons.add")}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </MainHeader>

              {/* Banner informativo de importación */}
              <Box style={{ padding: "0 20px 12px 20px" }}>
                <div style={{
                  borderRadius: 8,
                  border: "1px solid #00bcd4",
                  background: "linear-gradient(90deg, rgba(0,188,212,0.10) 0%, rgba(0,0,0,0.45) 100%)",
                  padding: "10px 16px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10
                }}>
                  <span style={{ fontSize: 18, marginTop: 1 }}>📋</span>
                  <div style={{ fontSize: "0.83rem", color: "#e0e0e0", lineHeight: 1.65 }}>
                    <span style={{ color: "#00e5ff", fontWeight: "bold", marginRight: 6 }}>{i18n.t("contactListItems.importBanner.title")}</span>
                    {i18n.t("contactListItems.importBanner.intro")}
                    <span style={{ color: "#ffd54f", fontWeight: "bold" }}>{i18n.t("contactListItems.importBanner.limit")}</span>
                    {i18n.t("contactListItems.importBanner.validationText")}
                    <span style={{ color: "#69f0ae", fontWeight: "bold" }}>{i18n.t("contactListItems.importBanner.activeWhatsapp")}</span>
                    {i18n.t("contactListItems.importBanner.deletionText")}
                    <span style={{ color: "#ce93d8" }}>{i18n.t("contactListItems.importBanner.intervalPrefix")}<strong>{i18n.t("contactListItems.importBanner.intervalBold")}</strong>{i18n.t("contactListItems.importBanner.intervalSuffix")}</span>
                    {i18n.t("contactListItems.importBanner.intervalEnd")}
                  </div>
                </div>
              </Box>

              <TableContainer className={classes.scrollArea}>
              <>
                {importing && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '45px 20px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    margin: '15px 0',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <CircularProgress size={52} style={{ marginBottom: '15px', color: '#3f51b5' }} />
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#3f51b5', textAlign: 'center' }}>
                      {i18n.t("contactListItems.importDialog.importingTitle")}
                    </span>
                    <span style={{ fontSize: '14px', color: '#555', marginTop: '8px', textAlign: 'center' }}>
                      {i18n.t("contactListItems.importDialog.importingSubtitle")}
                    </span>
                  </div>
                )}
                <input
                  style={{ display: "none" }}
                  id="upload"
                  name="file"
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={() => {
                    setConfirmOpen(true);
                  }}
                  ref={fileUploadRef}
                />
              </>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" style={{ width: "0%" }}>
                      #
                    </TableCell>
                    <TableCell>{i18n.t("contactListItems.table.name")}</TableCell>
                    <TableCell align="center">
                      {i18n.t("contactListItems.table.number")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("contactListItems.table.email")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("contactListItems.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell align="center" style={{ width: "0%" }}>
                          <IconButton>
                            {contact.isWhatsappValid ? (
                              <CheckCircleIcon
                                titleAccess="Whatsapp Válido"
                                htmlColor="green"
                              />
                            ) : (
                              <BlockIcon
                                titleAccess="Whatsapp Inválido"
                                htmlColor="grey"
                              />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{contact.name}</TableCell>
                        <TableCell align="center">{contact.number}</TableCell>
                        <TableCell align="center">{contact.email}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => hadleEditContact(contact.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          <Can
                            role={user.profile}
                            perform="contacts-page:deleteContact"
                            yes={() => (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfirmOpen(true);
                                  setDeletingContact(contact);
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={4} />}
                  </>
                </TableBody>
              </Table>
              </TableContainer>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 18px',
                background: '#fafafa',
                borderTop: '1px solid #e0e0e0',
                fontSize: '14px',
                color: '#555',
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px'
              }}>
                <div>
                  <strong>{i18n.t("contactListItems.pagination.showing")}:</strong> {contacts.length > 0 ? (pageNumber - 1) * 20 + 1 : 0} - {Math.min(pageNumber * 20, contactsCount)} {i18n.t("contactListItems.pagination.of")} {contactsCount} {i18n.t("contactListItems.pagination.contacts")}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                    style={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {i18n.t("contactListItems.pagination.prev")}
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => setPageNumber(prev => prev + 1)}
                    disabled={!hasMore || loading}
                    style={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {i18n.t("contactListItems.pagination.next")}
                  </Button>
                </div>
              </div>
            </Paper>
            <Dialog
              open={alertOpen}
              onClose={() => setAlertOpen(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              disableBackdropClick={true}
              disableEscapeKeyDown={true}
            >
              <DialogTitle id="alert-dialog-title" style={{ color: '#3f51b5', fontWeight: 'bold' }}>
                {alertTitle}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description" style={{ color: '#333', fontSize: '15px', whiteSpace: 'pre-line' }}>
                  {alertMessage}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAlertOpen(false)} color="primary" variant="contained" autoFocus>
                  Aceptar
                </Button>
              </DialogActions>
            </Dialog>
          </>}
    </MainContainer>
  );
};

export default ContactListItems;
